import React from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "../components/ui/button";
import { MapPin, Target, TrendingUp, Lightbulb, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

const Home = () => {
  return (
    <>
      <Helmet>
        <title>Consultant rentabilité boulangerie pâtisserie Annecy | Croissance Consulting</title>
        <meta name="description" content="Consultant en rentabilité pour boulangerie et pâtisserie à Annecy et France entière. J'aide les artisans à améliorer leur marge, leurs prix, leurs invendus et leur organisation." />
        <link rel="canonical" href="https://croissanceconsulting.net/" />
      </Helmet>

      <div className="min-h-screen bg-[#FBF7EF]">
        {/* Hero Section */}
        <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-7xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Left Content */}
              <div className="space-y-6 max-w-2xl">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#4A3A2A] font-poppins leading-tight">
                  Consultant rentabilité boulangerie/pâtisserie : marge, prix, invendus
                </h1>
                <p className="text-lg sm:text-xl text-[#2D2D2D] leading-relaxed">
                  J'accompagne les artisans boulangers/pâtissiers pour reprendre la main sur la marge, les prix, les invendus et l'organisation.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 items-start">
                  <Link to="/contact">
                    <Button className="bg-[#B65C3A] hover:bg-[#9d4e2f] text-white font-medium px-8 py-6 text-lg">
                      Proposer un échange
                    </Button>
                  </Link>
                </div>
                <div className="flex items-center space-x-2 text-[#6B7A3A] pt-4">
                  <MapPin size={20} />
                  <span className="text-sm font-medium">
                    Zone : Annecy • Savoie/Haute-Savoie (et + selon déplacement)
                  </span>
                </div>
              </div>

              {/* Right Image */}
              <div className="relative max-w-lg mx-auto lg:mx-0">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://customer-assets.emergentagent.com/job_6ea0b1d1-df5f-4368-91ed-bae07cdd7ca4/artifacts/thdsvw8d_photo%20profil.png"
                    alt="Christophe Marrama, consultant en boulangerie-pâtisserie"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#4A3A2A] font-poppins text-center mb-8">
              Pourquoi Croissance 🥐consulting
            </h2>
            <p className="text-lg text-[#2D2D2D] leading-relaxed text-center">
              Croissance, parce qu'une entreprise artisanale peut évoluer sans se renier. 🥐 parce que je viens du métier : je connais la réalité du fournil, du labo, de la boutique… et ce que ça fait quand les coûts montent et que la marge se fait grignoter. Mon approche est pragmatique : on mesure, on comprend, on agit.
            </p>
          </div>
        </section>

        {/* Croissants Image Section */}
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://customer-assets.emergentagent.com/job_diagnostic-fournil/artifacts/c1lnw9ev_croissants2.png"
                alt="Croissants au chocolat artisanaux glacés"
                className="w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#4A3A2A] font-poppins text-center mb-16">
              Ce que vous gagnez
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              <div className="bg-[#FBF7EF] rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-[#B65C3A]/10 rounded-lg flex items-center justify-center mb-4">
                  <Target className="text-[#B65C3A]" size={28} />
                </div>
                <h3 className="text-xl font-bold text-[#4A3A2A] font-poppins mb-3">
                  Clarté sur la marge
                </h3>
                <p className="text-[#2D2D2D]">
                  Vous comprenez enfin où part votre argent et quels produits vous font gagner de l'argent.
                </p>
              </div>

              <div className="bg-[#FBF7EF] rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-[#6B7A3A]/10 rounded-lg flex items-center justify-center mb-4">
                  <TrendingUp className="text-[#6B7A3A]" size={28} />
                </div>
                <h3 className="text-xl font-bold text-[#4A3A2A] font-poppins mb-3">
                  Moins d'invendus
                </h3>
                <p className="text-[#2D2D2D]">
                  Ajustez production et gamme pour réduire les pertes et améliorer la trésorerie.
                </p>
              </div>

              <div className="bg-[#FBF7EF] rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-[#B65C3A]/10 rounded-lg flex items-center justify-center mb-4">
                  <Lightbulb className="text-[#B65C3A]" size={28} />
                </div>
                <h3 className="text-xl font-bold text-[#4A3A2A] font-poppins mb-3">
                  Décisions simples
                </h3>
                <p className="text-[#2D2D2D]">
                  Des actions concrètes et mesurables, sans théorie inutile ni paperasse.
                </p>
              </div>

              <div className="bg-[#FBF7EF] rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-[#6B7A3A]/10 rounded-lg flex items-center justify-center mb-4">
                  <ShieldCheck className="text-[#6B7A3A]" size={28} />
                </div>
                <h3 className="text-xl font-bold text-[#4A3A2A] font-poppins mb-3">
                  Sérénité retrouvée
                </h3>
                <p className="text-[#2D2D2D]">
                  Vous reprenez le contrôle de votre entreprise, sans stress ni épuisement.
                </p>
              </div>

              <div className="bg-[#FBF7EF] rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-[#B65C3A]/10 rounded-lg flex items-center justify-center mb-4">
                  <Target className="text-[#B65C3A]" size={28} />
                </div>
                <h3 className="text-xl font-bold text-[#4A3A2A] font-poppins mb-3">
                  Prix justes
                </h3>
                <p className="text-[#2D2D2D]">
                  Définissez des prix cohérents qui reflètent votre valeur et protègent votre rentabilité.
                </p>
              </div>

              <div className="bg-[#FBF7EF] rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-[#6B7A3A]/10 rounded-lg flex items-center justify-center mb-4">
                  <TrendingUp className="text-[#6B7A3A]" size={28} />
                </div>
                <h3 className="text-xl font-bold text-[#4A3A2A] font-poppins mb-3">
                  Trésorerie améliorée
                </h3>
                <p className="text-[#2D2D2D]">
                  Augmentez la rentabilité et libérez de la trésorerie pour investir et respirer.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Diagnostic Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#4A3A2A] font-poppins text-center mb-8">
              Diagnostic immersion boulangerie/pâtisserie
            </h2>
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <p className="text-lg text-[#2D2D2D] leading-relaxed mb-6">
                Je viens dans votre fournil ou laboratoire pendant 2 à 3 heures pour observer, mesurer et comprendre votre réalité terrain : production, gamme, organisation, flux, équipe.
              </p>
              <p className="text-lg text-[#2D2D2D] leading-relaxed mb-6">
                Ensuite, j'analyse vos chiffres : marge par produit, invendus, achats, prix.
              </p>
              <p className="text-lg text-[#2D2D2D] leading-relaxed mb-6">
                Vous recevez sous 48h un diagnostic clair avec 3 à 5 leviers d'action concrets et un plan 7-30 jours.
              </p>
              
              {/* Lien interne vers Diagnostic Immersion Terrain */}
              <div className="bg-[#6B7A3A]/5 rounded-xl p-4 mb-6">
                <p className="text-[#2D2D2D] flex items-center gap-2">
                  <span className="text-xl">👉🏼</span>
                  <Link to="/audit-rentabilite-boulangerie" className="text-[#B65C3A] hover:underline font-medium">
                    Diagnostique terrain boulangerie/pâtisserie : voir le déroulé complet
                  </Link>
                </p>
              </div>

              <div className="text-center">
                <Link to="/offres">
                  <Button className="bg-[#B65C3A] hover:bg-[#9d4e2f] text-white font-medium px-8 py-4 text-lg">
                    Découvrir les offres
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-3xl text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#4A3A2A] font-poppins mb-6">
              Prêt à reprendre la main sur votre marge ?
            </h2>
            <p className="text-lg text-[#2D2D2D] mb-8">
              Parlons de votre situation. Un échange sans engagement pour voir ensemble ce qui est possible.
            </p>
            <Link to="/contact">
              <Button className="bg-[#B65C3A] hover:bg-[#9d4e2f] text-white font-medium px-10 py-6 text-lg">
                Proposer un échange
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;
